import React, {
  Dispatch,
  KeyboardEvent,
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { Set } from 'immutable';
import {
  fetchMoreGroupMembers,
  refetchGroupMembersPage,
  selectGroup,
  selectGroupIsImporting,
  updateGroup,
} from '@actions/groups';
import { modalStateSelector, selectSavedViewState, selectSelectedContactBatch } from '@actions/ui';
import * as actions from '@actions/groupView';
import { metaSymbol, metaTrigger } from '@support/keyboardHandler';
import isModifiedEvent from '@support/isModifiedEvent';
import { GroupMembershipCount } from './Header';
import ManageContacts from './ManageContacts';
import styles from './Group.module.scss';
import Contact from '@models/Contact';
import Group from '@models/Group';
import buildGroupActions from '@views/CommandBar/actionsBySection/groupActions';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import analytics from '@support/analytics';
import { useHotkeyHandler } from '@hooks/use-hotkey-handler';
import { useAppContactSelecting } from '@hooks/use-app-contact-selecting';
import { useNavigationConfirmation } from '@hooks/use-navigation-confirmation';
import ColorSelect from './ColorSelect';
import { useMountedEffect } from '@support/hooks/use-mounted-effect';
import EmptyGroup from './EmptyGroup';
import { fetchGroupShareStatus } from '@actions/shareStatuses/groups';
import { selectGroupShareStatus } from '@reducers/data/shareStatuses/groups';
import GroupReconnect from '@views/Group/GroupReconnect';
import ResultsDisplay from '@views/Search/ResultsDisplay';
import { ID, IDRecord } from '@app/types';
import { selectContactGetter } from '@actions/contacts';
import GroupIdentifierIcon from '@components/GroupIdentifierIcon';
import ShareGroupBtn from './Header/ShareGroupBtn';
import Header, { HeaderActionProps } from '@components/Header';
import { CheckIcon, LoaderIcon, PlusIcon } from '@components/Icons';
import { useScrollRestoration } from '@support/hooks/use-scroll-restoration';
import { useSaveViewState } from '@support/hooks/use-save-view-state';
import { ViewID } from '@reducers/ui/viewRestoration';
import { useAuthContext } from '@support/hooks/use-auth-context';
import { Link } from 'react-router-dom';
import KeyboardShortcut from '@components/KeyboardShortcut';
import { selectNavBarState } from '@reducers/ui/navBar';
import { useWorkspaceContext } from '@support/hooks/use-workspace-context';

/* TODO:
    - add handling for empty list title when trying to save (form validation error)
		- communicate other error states
*/

export const unsavedChangesMsg = (groupInState: Group, groupInStore: Group) => {
  if (groupInState.isPersisted) {
    return `You ${groupInStore.diffText(groupInState)}. If you exit you will lose these changes.`;
  } else {
    return "You haven't saved this Group. If you exit, it will be discarded.";
  }
};

export const GroupTitle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const groupByID = useSelector(selectGroup(id));
  const isImporting = useSelector(selectGroupIsImporting(id));
  const { group: groupInView } = useSelector(actions.selectGroupView);
  const group = groupInView.title ? groupInView : groupByID;
  const loc = useLocation();
  const isOnHighlights = loc.pathname.includes('highlights');
  const isOnMembers = !isOnHighlights;

  useHotkeyHandler(
    {
      handler: (e) => {
        if (e.defaultPrevented) {
          return;
        }

        if (e.key === '1' && isOnHighlights) {
          navigate(`/group/${group.slug || group.id}`);
        } else if (e.key === '2' && group.memberIDs.size > 0 && isOnMembers) {
          navigate(`/group/${group.slug || group.id}/highlights`);
        }
      },
    },
    [group.memberIDs.size, group.id, navigate],
  );

  let activityTab = null;
  if (group.memberIDs.size > 0 && !isImporting) {
    activityTab = (
      <div className={cn(styles.tab, isOnHighlights && styles.active)}>
        <KeyboardShortcut
          placement="bottom"
          shortcut={<KeyboardShortcut.Key>2</KeyboardShortcut.Key>}
        >
          <Link to={`/group/${group.slug || group.id}/highlights`} className={cn(styles.tabTitle)}>
            Highlights
          </Link>
        </KeyboardShortcut>
      </div>
    );
  }

  return (
    // using `key` here fixes a bug in Safari where the color doesn't come in if the app mounts on a group page
    <div
      key={group.color}
      className={cn(styles.groupHeader, group.is_internal && styles.starred)}
      style={{ '--groupColor': group.color } as React.CSSProperties}
    >
      <GroupIdentifierIcon group={group} className={styles.dot} />
      <div className={cn(styles.tab, styles.groupName, isOnMembers && styles.active)}>
        <KeyboardShortcut
          placement="bottom"
          shortcut={<KeyboardShortcut.Key>1</KeyboardShortcut.Key>}
          hideTooltip={group.memberIDs.size === 0}
        >
          <Link to={`/group/${group.slug || group.id}`} className={cn(styles.tabTitle)}>
            {group.title}
          </Link>
        </KeyboardShortcut>
      </div>
      {(isImporting && group.allContactsLoaded) && <span className={styles.processingBadge}>Processing</span>}
      {isOnMembers && !group.allContactsLoaded ? <LoaderIcon /> : activityTab}
    </div>
  );
};

const HA = (props: HeaderActionProps) => <Header.Action as="span" tooltip hover {...props} />;

const EditableGroupHeader = ({
  group,
  hasNoChanges,
  cancelEditing,
  onChange,
  saveGroup,
  loading,
  onAddPeopleClick,
  isSaving,
}: {
  group: Group;
  hasNoChanges: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  saveGroup: () => void;
  onAddPeopleClick: () => void;
  loading?: boolean;
  cancelEditing?: () => void;
  isSaving?: boolean;
}) => {
  const { status } = useSelector(selectNavBarState);
  const handleKeyDown: KeyboardEventHandler = (e) => {
    if (e.key == 'Enter' && !loading) {
      saveGroup();
      e.preventDefault();
      e.stopPropagation();
    } else if (e.key == 'Escape' && hasNoChanges) {
      cancelEditing?.();
    }
  };

  return (
    <Header
      className={cn(
        styles.editableGroupHeader,
        group.is_internal && 'styles-starred',
        styles[`navBar-${status}`],
      )}
      goBack={false}
      style={{ borderBottomColor: group?.color }}
      actions={
        isSaving ? (
          <LoaderIcon />
        ) : (
          <>
            <HA
              shortcut="A"
              title="Add People"
              IconComponent={() => (
                <PlusIcon width={17} height={17} strokeWidth={1.8} hoverOpacity />
              )}
              onClick={onAddPeopleClick}
            />
            <HA
              title="Save Changes"
              shortcut={[metaSymbol, `Enter`]}
              onClick={saveGroup}
              IconComponent={() => <CheckIcon hoverOpacity />}
            />
          </>
        )
      }
    >
      <GroupIdentifierIcon group={group} className={styles.color} />
      <input
        autoFocus
        type="text"
        required
        placeholder="New Group"
        className={`${styles.input} text-contact`}
        value={group.title || ''}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        disabled={isSaving}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
      />
    </Header>
  );
};

export function GroupDisplay({
  group,
  isEditing,
  isEditingContacts,
  isImporting,
  saveGroup,
  updateGroupInView,
  referenceGroup,
  startGroupContactEditing,
  stopGroupContactEditing,
  setHighlightedItem,
  highlightedItem,
  loadNextPage,
  scrollingContainerRef,
  handleContactHotKey,
  stopGroupEditing,
  dropdownMenuOptions,
  defaultHighlightedItem,
  isSaving,
  resetHighlightOnLoad,
}: {
  group: Group;
  isEditing: boolean;
  isEditingContacts: boolean;
  isImporting?: boolean;
  saveGroup: () => void;
  updateGroupInView: (group: Group) => void;
  referenceGroup: Group;
  startGroupContactEditing: () => void;
  stopGroupContactEditing: () => void;
  setHighlightedItem?: Dispatch<Contact>;
  highlightedItem?: Contact;
  loadNextPage?: () => void;
  scrollingContainerRef?: React.MutableRefObject<any>;
  handleContactHotKey?: (contact: IDRecord, e: KeyboardEvent) => void;
  stopGroupEditing?: () => void;
  dropdownMenuOptions?: any;
  defaultHighlightedItem?: Contact;
  isSaving?: boolean;
  resetHighlightOnLoad?: boolean;
}) {
  const removeContact = (id: ID) => updateGroupInView(group.removeContact(id));
  const addContact = (id: ID) => updateGroupInView(group.addContact(id));
  const addOrRemoveContact = useCallback(
    (contact: Contact) => {
      if (group.hasContact(contact.id)) {
        removeContact(contact.id);
      } else {
        addContact(contact.id);
      }
    },
    [group],
  );

  return (
    <>
      <ResultsDisplay
        key={group.slug || group.id}
        // might be able to simplify this.. different selector and/or coerce
        search={{
          type: 'groupSearch',
          results: group,
          term: 'groupSearchTerm',
          sorting: group.sorting,
          deletedIDCacheToExpire: Set<ID>(),
          resourceID: group.slug || group.id,
          pauseLoadingMore: isEditing || isEditingContacts,
        }}
        setHighlightedItem={setHighlightedItem}
        defaultHighlightedItem={defaultHighlightedItem}
        defaultHighlightedIdx={1}
        highlightedItem={highlightedItem}
        loadNextPage={loadNextPage}
        onHotKey={handleContactHotKey}
        ref={scrollingContainerRef}
        disabled={isEditing}
        emptyStateUI={<EmptyGroup id={group.id} />}
        listHeader={group.memberIDs.size > 0 ? <GroupMembershipCount group={group} /> : undefined}
        sidebar={
          isEditing ? (
            <div className={cn('content-container', isSaving && styles.editingDisabled)}>
              <ColorSelect
                color={group.color}
                onSelect={(color) => updateGroupInView(group.copyWith({ color }))}
              />
              <GroupReconnect
                cadence={group.reconnect_cadence}
                onSelect={(cadence) =>
                  updateGroupInView(group.copyWith({ reconnect_cadence: cadence }))
                }
              />
            </div>
          ) : undefined
        }
        title={
          isEditing ? (
            <EditableGroupHeader
              saveGroup={saveGroup}
              hasNoChanges={!group.isDifferent(referenceGroup)}
              cancelEditing={stopGroupEditing}
              onChange={(e) => updateGroupInView(group.copyWith({ title: e.target.value }))}
              group={group}
              onAddPeopleClick={startGroupContactEditing}
              isSaving={isSaving}
            />
          ) : (
            <GroupTitle />
          )
        }
        dropdownMenuOptions={(isEditing || isImporting) ? undefined : dropdownMenuOptions}
        beforeHeaderActions={isImporting ? undefined : <ShareGroupBtn groupID={group.id} />}
        resetListHighlightOnSearch={resetHighlightOnLoad}
      />

      {isEditingContacts && (
        <ManageContacts
          group={group}
          open={true}
          onSelect={addOrRemoveContact}
          onClose={stopGroupContactEditing}
        />
      )}
    </>
  );
}

function ExistingGroup({ id }) {
  const viewID = `Group_${id}` as ViewID;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { contactIsRestricted, getEntitlementValue } = useAuthContext();
  const { userIsAdmin } = useWorkspaceContext();
  const getContact = useSelector(selectContactGetter);
  const groupInStore: Group = useSelector(selectGroup(id));
  const groupIsImporting = useSelector(selectGroupIsImporting(id));
  const selectedContactBatch = useSelector(selectSelectedContactBatch);
  const groupShareStatus = useSelector(selectGroupShareStatus(id));
  const savedViewState = useSelector(selectSavedViewState(viewID));
  const groupView = useSelector(actions.selectGroupView);
  const { isOpen: modalIsOpen } = useSelector(modalStateSelector);

  const { group, isEditing, isEditingContacts } = groupView;
  const [highlightedItem, setHighlightedItem] = useState(savedViewState?.selectedContact);
  const scrollingContainerRef = useRef(null);
  const defaultHighlightedItem = useMemo(() => highlightedItem, []);

  const loadNextPage = () => {
    dispatch(fetchMoreGroupMembers(id));
  };

  const dropdownMenuOptions = buildGroupActions(
    { groupView: { ...groupView, isShared: !!groupShareStatus?.isShared }, selectedContactBatch },
    dispatch,
    navigate,
    userIsAdmin,
    getEntitlementValue,
    contactIsRestricted,
    getContact,
  );

  // Actions that update the view state stored in redux
  const setGroupWithFetchedData = (group: Group) => {
    dispatch(actions.setGroupInView(group));
  };
  const updateGroupInView = (group: Group) => {
    dispatch(actions.updateGroupInView(group));
  };
  const startGroupEditing = () => dispatch(actions.startEditing);
  const stopGroupEditing = () => dispatch(actions.stopEditing);
  const startGroupContactEditing = () => dispatch(actions.startContactEditing);
  const stopGroupContactEditing = () => dispatch(actions.stopContactEditing);
  const resetGroup = () => dispatch(actions.resetGroup);

  const handleContactHotKey = (contact: IDRecord, e: KeyboardEvent) => {
    if (modalIsOpen || !contact?.id) {
      return;
    }

    if (e.key == 'Backspace' && !metaTrigger(e)) {
      if (selectedContactBatch.size > 0) {
        updateGroupInView(group.removeContactIDSet(selectedContactBatch));
      }

      contact?.id && updateGroupInView(group.removeContact(contact.id));
    }
  };

  const saveGroup = useCallback(() => {
    if (groupInStore.isDifferent(group)) {
      dispatch(updateGroup({ group, requestPayload: groupInStore.diff(group) }));
      analytics.track('Group Updated');
    }
    stopGroupEditing();
  }, [group]);

  useScrollRestoration({ viewID, scrollingContainerRef });

  useSaveViewState(viewID, { selectedContact: highlightedItem }, [highlightedItem]);

  useAppContactSelecting({ contact: highlightedItem, source: 'group' });

  useEffect(() => setGroupWithFetchedData(groupInStore), [groupInStore.updated]);

  useEffect(() => {
    dispatch(refetchGroupMembersPage(id));

    if (id === 'starred') {
      dispatch(fetchGroupShareStatus(groupInStore.id));
    } else {
      dispatch(fetchGroupShareStatus(id));
    }
  }, [id]);

  useMountedEffect(() => {
    if (isEditing || isEditingContacts || group.isFetching || !groupInStore.isDifferent(group)) {
      return () => {};
    }
    let timer = setTimeout(() => saveGroup(), 500);
    return () => clearTimeout(timer);
  }, [group.memberIDs, isEditing, isEditingContacts]);

  useHotkeyHandler(
    {
      handler: (e) => {
        if (window.__activeConfirm || groupIsImporting) {
          return;
        }

        if (e.key === 'e' && !isEditing) {
          e.preventDefault();
          startGroupEditing();
        } else if ((!isModifiedEvent(e) && e.key === 'a') || e.key === '+') {
          e.preventDefault();
          startGroupContactEditing();
        } else if (isEditing && metaTrigger(e) && e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          saveGroup();
        }
      },
    },
    [isEditing, group, groupIsImporting],
  );

  useNavigationConfirmation(
    {
      hasUnsavedChanges: isEditing && groupInStore.isDifferent(group),
      unsavedChangesMsg: unsavedChangesMsg(group, groupInStore),
      saveChanges: saveGroup,
      onExit: stopGroupEditing,
      backBtnOverrideFn:
        isEditing &&
        (() => {
          resetGroup();
        }),
    },
    [group, isEditing, groupInStore],
  );

  // TBD if this should be resurrected
  // useOnVisible(() => {
  //   dispatch(fetchGroup({ id, isBackgroundSync: true }));
  // });

  return (
    <GroupDisplay
      group={group}
      isEditing={isEditing}
      isEditingContacts={isEditingContacts}
      isImporting={groupIsImporting}
      saveGroup={saveGroup}
      updateGroupInView={updateGroupInView}
      referenceGroup={groupInStore}
      startGroupContactEditing={startGroupContactEditing}
      stopGroupContactEditing={stopGroupContactEditing}
      setHighlightedItem={setHighlightedItem}
      highlightedItem={highlightedItem}
      loadNextPage={loadNextPage}
      scrollingContainerRef={scrollingContainerRef}
      handleContactHotKey={handleContactHotKey}
      stopGroupEditing={stopGroupEditing}
      dropdownMenuOptions={dropdownMenuOptions}
      defaultHighlightedItem={defaultHighlightedItem}
      resetHighlightOnLoad={!highlightedItem}

    />
  );
}

export default function GroupView() {
  const { id } = useParams();
  const groupInStore: Group = useSelector(selectGroup(id));

  return <ExistingGroup key={`${id}${groupInStore.allContactsLoaded ? '_loaded' : ''}`} id={id} />;
}
